import React, {useEffect, useState} from 'react';
import {TranscriptionPopover} from 'app/components/features/transcribing';
import {TranscribingStartStopButton} from 'app/components/sharedReactComponents/ActionButtons/StartStopButton/TranscribingStartStopButton';
import {DEVICE_START_STOP_BUTTON_STATUS} from 'app/components/sharedReactComponents/ActionButtons/StartStopButton/constants';
import {useDeviceTranscriptionTime} from 'app/components/sharedReactComponents/TranscribeDuration/useDeviceTranscriptionTime';
import {TranscribingStatus} from 'app/components/DeviceDetails/constants';
import {LiveScryptDeviceModel} from 'app/components/DeviceDetails/Models/LiveScryptDeviceModel';

type State = 'started' | 'paused' | 'stopped' | 'loading' | 'error';

function getButtonState(status: TranscribingStatus): State {
  switch (status) {
    case TranscribingStatus.Started:
    case TranscribingStatus.Paused:
      return status;

    default:
      return TranscribingStatus.Stopped;
  }
}

interface Props {
  device: LiveScryptDeviceModel;
  paymentAccess?: boolean;
}

export function TranscribingButton({device, paymentAccess}: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [state, setState] = useState<State>('stopped');

  const cap = device.caps.transcribing;

  useEffect(() => {
    const state = getButtonState(device.getTranscribingStatus());
    setState(state);
  }, [device]);

  const transcribingInfo = device.getTranscribingInfo();

  const {startTime, receivedTime} = useDeviceTranscriptionTime({
    deviceId: device.getId(),
    sessionId: transcribingInfo.sessionId,
    status: transcribingInfo.status,
  });

  const handleClick = async (e: React.MouseEvent<HTMLElement>) => {
    if (!cap) {
      return;
    }

    const status = device.getTranscribingStatus();

    if (status === TranscribingStatus.Started) {
      return device.stopTranscribing();
    }

    if (cap.enabled === false) {
      setAnchorEl(e.currentTarget);
      return;
    }

    setState(DEVICE_START_STOP_BUTTON_STATUS.LOADING);

    if (status === TranscribingStatus.Paused) {
      return device.resumeTranscribing();
    }

    return device.startTranscribing();
  };

  if (!cap) {
    return null;
  }

  return (
    <>
      <TranscribingStartStopButton
        status={state as DEVICE_START_STOP_BUTTON_STATUS}
        startTime={startTime}
        receivedTime={receivedTime}
        onClick={handleClick}
      />

      <TranscriptionPopover
        capability={cap}
        anchorEl={anchorEl}
        paymentAccess={paymentAccess ?? false}
        setAnchorEl={setAnchorEl}
      />
    </>
  );
}
